import { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'

/**
 * Defines an invitation information to a different event in the current workspace
 */
export interface InvitationInfo {
  /** Event registrations are getting invited to */
  eventSlug: string
  /** Persona registrations are getting invited to */
  formSlug: string
  /** Invitation message */
  message: string
  /** Event registrations are getting invited from */
  fromEventSlug: string
  /** Persona registrations are getting invited from */
  fromFormSlug: string
  /** Invitation action - all - include - exclude - test */
  action: 'all' | 'include' | 'exclude' | 'test' | string
  /** Invitation ids - can be empty if the action is all */
  ids: string[]
  /** Invitation email template - can can be either 1 or 2 or 3 */
  selectedTemplate: number
  /** Invitation emails - can be empty in case no test email being sent */
  emails: string[]
  isInvitationModal?: boolean
  selectionCount?: number
  /** Parent ID for exclude action - used when excluding child registrations */
  parent_id?: string
  /** Serialized search / filter query used for the listing (optional) */
  search?: string
  /** Custom invite buttons (confirm / decline) with editable text and colors (optional, for custom template) */
  customButtons?: CustomInviteButtonConfig
  /** Optional image used in the custom template (preview URL) */
  imageUrl?: string
  /** Optional card background color (hex) for custom template */
  cardBackgroundColor?: string
  /** Optional Add to Calendar button background color (hex) for custom template */
  calendarButtonColor?: string
  /** Delivery channel for the invitation (email, WhatsApp, SMS) */
  inviteChannel?: NotificationChannel
  /** Optional font family CSS stack for custom template */
  fontFamily?: string
  subject?: string
}

/**
 * Builds a full InvitationInfo from shared defaults plus call-site overrides.
 * Keeps data-header / create (and future hosts) from drifting empty-payload copies.
 */
export function buildInvitationInfo(overrides: Partial<InvitationInfo> = {}): InvitationInfo {
  return {
    eventSlug: '',
    formSlug: '',
    message: '',
    fromEventSlug: '',
    fromFormSlug: '',
    action: '',
    ids: [],
    emails: [],
    selectedTemplate: 1,
    isInvitationModal: true,
    ...overrides
  }
}

/** Config for the two custom invite buttons shown under the editor (confirm / decline). */
export interface CustomInviteButtonConfig {
  confirm: { text: string; textColor: string; backgroundColor: string; isBold?: boolean; isItalic?: boolean; isUnderline?: boolean; fontSize?: string }
  decline: { text: string; textColor: string; backgroundColor: string; isBold?: boolean; isItalic?: boolean; isUnderline?: boolean; fontSize?: string; hidden?: boolean }
}
/** Shared PATCH fields for invite-bulk and invite-test (message, template, channel, optional custom UI). */
export interface InviteContentPayload {
  invite_message: string
  invite_event: string
  invite_form: string
  email_template: number
  /** Delivery channel: email, whatsapp, or sms */
  send_method: 'email' | 'whatsapp' | 'sms'
  /** Optional image URL from the custom template */
  image_url?: string
  /** Optional card background color (hex) */
  card_background?: string
  /** Optional Add to Calendar button background color (hex) */
  calender_button?: string
  /** Optional font family CSS stack for custom template */
  font_family?: string
  confirm_background_color?: string
  confirm_text_color?: string
  confirm_invitation_text?: string
  confirm_is_italic?: boolean
  confirm_is_bold?: boolean
  confirm_is_underline?: boolean
  confirm_font_size?: string
  decline_background_color?: string
  decline_text_color?: string
  decline_invitation_text?: string
  decline_is_italic?: boolean
  decline_is_bold?: boolean
  decline_is_underline?: boolean
  decline_font_size?: string
  subject?: string
}

export interface InvitePayload extends InviteContentPayload {
  action: string
  ids: string[]
  emails: string[]
  count: number
  parent_id?: string
  search?: string
}

/** PATCH invite-test: same content as bulk, plus test receiver. */
export interface InviteTestPayload extends InviteContentPayload {
  receiver: string
}

export enum InvitationState {
  TO_EVENT,
  TO_PERSONA,
  FROM_EVENT,
  FROM_PERSONA,
  /** Pick Email / WhatsApp / SMS before composing the message */
  CHANNEL,
  MESSAGE,
  COMPLETE
}
